/**
 * "Search By Activity Name" in the activity results filters (TRAV23-5158).
 *
 * A copy of the activities front's `SearchPropertyName`, wired to the activity slices.
 * The merged app kept using RSI's common `SearchPropertyName`, which is the hotel one:
 * it posts to `/hotels/hotel/autocomplete` and writes the hotel `filtersStore`, so the
 * field never found an activity and never reached the `/Activities/search` body. The
 * two are the same screen over different data, hence a copy rather than a prop on
 * RSI's - that one is a connected class component shared by every hotel surface, and
 * threading a thunk plus a slice through it would touch all of them.
 *
 * Markup and class names stay identical to RSI's, so the existing `hotel-dropdown`
 * styles apply unchanged.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Highlighter from 'react-highlight-words';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { AutoComplete, Select } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { faBuilding, faWalking } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Magnifier } from '@assets';
import { ENTER_KEY } from '@share/constants';
import { IHotelAutocompleteItem } from '@share/common-types';
import { activityFiltersActions, getActivitiesAutocomplete } from '@share/store/slices';
import { AppDispatch, RootState } from '@share/utils';
import { NULL_VALUE, UNDEFINED_VALUE } from '@constants';

import '../../common/search-property-name/style.scss';

const { Option, OptGroup } = Select;

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE = 500;
const LIST_HEIGHT = 400;
const ZERO = 0;

interface IProps {
  title: string;
  onSaveValue: () => void;
  className?: string;
  isMapView?: boolean;
  disabled?: boolean;
}

export const ActivitySearchPropertyName: React.FC<IProps> = ({
  title,
  onSaveValue,
  className = '',
  isMapView = false,
  disabled = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const activityFiltersStore = useSelector((state: RootState) => state.activityFiltersStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const { autocompleteItems, autocompleteLoading, selectedAutocompleteLabel } =
    activityFiltersStore;

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isFocused, setFocused] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastSearchRef = useRef('');
  const lastSelectedLocationRef = useRef<IHotelAutocompleteItem | null>(UNDEFINED_VALUE);
  const lastSelectedValueRef = useRef(selectedAutocompleteLabel || '');

  const doSearch = useMemo(
    () =>
      debounce((search: string) => {
        dispatch(getActivitiesAutocomplete(search));
      }, SEARCH_DEBOUNCE),
    [dispatch],
  );

  const onSearch = useCallback(
    (search: string) => {
      lastSearchRef.current = search;
      setDropdownOpen(!!search);

      if (search.length >= MIN_SEARCH_LENGTH) {
        doSearch(search);
      } else {
        dispatch(activityFiltersActions.setAutocompleteItems([]));
      }
    },
    [dispatch, doSearch],
  );

  /**
   * Clearing the field clears `propertyName` as well: the search body reads the
   * selected item but the filter chips read the name, and they must not disagree.
   */
  const onChange = useCallback(
    // antd 4 types the option loosely and calls back with `undefined` on clear.
    (value: string, option: any) => {
      if (!value) {
        setDropdownOpen(false);
        dispatch(activityFiltersActions.selectAutocompleteLabel(''));
        dispatch(activityFiltersActions.setAutocompleteItems([]));
        dispatch(activityFiltersActions.selectAutocompleteItem(NULL_VALUE));
        dispatch(activityFiltersActions.setPropertyName(''));
        lastSelectedLocationRef.current = NULL_VALUE;
        lastSelectedValueRef.current = '';
        onSaveValue();
        return;
      }

      const strValue = String(value);
      const activity = autocompleteItems.find(({ hotelName }) => hotelName === option?.key);

      setDropdownOpen(false);
      dispatch(activityFiltersActions.selectAutocompleteLabel(strValue));
      dispatch(activityFiltersActions.setAutocompleteItems([]));
      dispatch(activityFiltersActions.selectAutocompleteItem(activity || NULL_VALUE));
      lastSelectedLocationRef.current = activity || NULL_VALUE;

      if (activity && lastSelectedValueRef.current !== strValue) {
        lastSelectedValueRef.current = strValue;
        dispatch(activityFiltersActions.setPropertyName(strValue));
        onSaveValue();
      }
    },
    [autocompleteItems, dispatch, onSaveValue],
  );

  /**
   * A transparent layer covers each row so the whole row is a target, which means the
   * option's own click never fires. `mousedown`, not `click`: the field blurs first and
   * antd tears the list down before a click would land.
   */
  const onOptionMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const name = (event.target as HTMLElement)?.getAttribute('data-item');

      if (!name) {
        return;
      }

      const activity = autocompleteItems.find(({ hotelName }) => hotelName === name);

      setDropdownOpen(false);
      dispatch(activityFiltersActions.selectAutocompleteLabel(name));
      dispatch(activityFiltersActions.selectAutocompleteItem(activity || NULL_VALUE));
      dispatch(activityFiltersActions.setAutocompleteItems([]));
      dispatch(activityFiltersActions.setPropertyName(name));
      lastSelectedLocationRef.current = activity || NULL_VALUE;
      lastSelectedValueRef.current = name;
      onSaveValue();
    },
    [autocompleteItems, dispatch, onSaveValue],
  );

  const onFocus = useCallback(() => {
    setFocused(true);
    onSearch(activityFiltersStore.selectedAutocompleteItem?.hotelName || '');
  }, [activityFiltersStore.selectedAutocompleteItem, onSearch]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        e.code === ENTER_KEY &&
        isDropdownOpen &&
        isFocused &&
        autocompleteItems?.length &&
        !lastSelectedLocationRef.current
      ) {
        const activity = autocompleteItems[ZERO];

        setDropdownOpen(false);
        dispatch(activityFiltersActions.selectAutocompleteLabel(activity.hotelName));
        dispatch(activityFiltersActions.selectAutocompleteItem(activity));
        dispatch(activityFiltersActions.setAutocompleteItems([]));
        dispatch(activityFiltersActions.setPropertyName(activity.hotelName));
        lastSelectedLocationRef.current = activity;
        lastSelectedValueRef.current = activity.hotelName;
        onSaveValue();
      }
    },
    [autocompleteItems, dispatch, isDropdownOpen, isFocused, onSaveValue],
  );

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        dispatch(activityFiltersActions.setAutocompleteItems([]));
        lastSearchRef.current = '';
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dispatch]);

  const notFoundContent = useMemo(() => {
    let message: React.ReactNode;

    if ((selectedAutocompleteLabel?.length || ZERO) < MIN_SEARCH_LENGTH) {
      message = <FormattedMessage id="search.min.symbols" />;
    } else if (autocompleteLoading) {
      message = <FormattedMessage id="searching" />;
    } else {
      message = <FormattedMessage id="nothing.found" />;
    }

    return <div className="hotel-dropdown__nothing-found">{message}</div>;
  }, [autocompleteLoading, selectedAutocompleteLabel]);

  const options = useMemo(() => {
    if (!autocompleteItems?.length) {
      return null;
    }

    return (
      <OptGroup label={<FormattedMessage id="activities" />}>
        {autocompleteItems.map((item: IHotelAutocompleteItem) => (
          <Option key={item.hotelName} value={item.hotelName} className="hotel-dropdown__option">
            <div
              className="hotel-dropdown__option-background"
              data-item={item.hotelName}
              onMouseDown={onOptionMouseDown}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 2000,
              }}
            />
            <div className="hotel-dropdown__option-icon">
              <FontAwesomeIcon
                icon={item.activityCode ? faWalking : faBuilding}
                style={{ color: '#D2D2D2' }}
              />
            </div>
            <div className="hotel-dropdown__location-name">
              <Highlighter
                highlightClassName="bold-text"
                searchWords={[lastSearchRef.current.trim()]}
                autoEscape={true}
                textToHighlight={item.hotelName}
              />
            </div>
          </Option>
        ))}
      </OptGroup>
    );
  }, [autocompleteItems, onOptionMouseDown]);

  const generalBorderRadius = loginStore.account?.generalBorderRadius;

  return (
    <div className={`hotel-dropdown ${className}`} ref={wrapperRef}>
      <p className="hotel-dropdown__title secondary-font">
        <FormattedMessage id={title} />
      </p>

      <div style={{ position: 'relative' }}>
        <AutoComplete
          onKeyDown={onKeyDown}
          virtual={false}
          showSearch={true}
          className={!isEmpty(generalBorderRadius) ? `border-radius-${generalBorderRadius}` : ''}
          placeholder={
            <FormattedMessage
              id={isMapView ? 'map.search.placeholder' : 'location.input.placeholder'}
            />
          }
          disabled={disabled}
          onChange={onChange}
          onSearch={onSearch}
          onFocus={onFocus}
          onBlur={() => setFocused(false)}
          showArrow={false}
          open={isDropdownOpen}
          defaultActiveFirstOption={false}
          filterOption={() => true}
          autoClearSearchValue={false}
          listHeight={LIST_HEIGHT}
          allowClear={true}
          value={selectedAutocompleteLabel}
          notFoundContent={notFoundContent}
        >
          {options}
        </AutoComplete>

        <div className={`hotel-dropdown__search-icon ${isFocused ? 'focused' : ''}`}>
          <Magnifier />
        </div>
      </div>
    </div>
  );
};
